import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  ProgressBar,
  Alert,
  Spinner,
  Breadcrumb,
  Tooltip,
  OverlayTrigger,
  Dropdown
} from "react-bootstrap";
import "../common.css";
import { useRouteMatch, useHistory, Route, Switch } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, 
  faClock,
  faFileContract,
  faUserTie,
  faClipboardCheck,
  faFileAlt,
  faHandshake,
  faPrint,
  faCopy,
  faLanguage
} from '@fortawesome/free-solid-svg-icons';

import SavedContracts from "./saved-contracts";
import { roles, contractTypes, steps, wizardLabels, getFormFields, supportedLanguages } from './data';
import ContractDetailsModal from './ContractDetailsModal';
import ContractForm from './ContractForm';
import { generateContract } from "../../../service/workflow-api";
import { printContract, copyToClipboard } from '../../../utils/contractUtils';

const ContractGenerator = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [userRole, setUserRole] = useState("");
  const [contractType, setContractType] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [contractJson, setContractJson] = useState({});
  const [savedContracts, setSavedContracts] = useState([]);
  const [showSavedContractsModal, setShowSavedContractsModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const { path, url } = useRouteMatch();
  const history = useHistory();

  // Load saved contracts from localStorage on component mount
  useEffect(() => {
    
  }, []);
  
  // Load a saved contract
  const loadContract = (contract) => {
    setUserRole(contract.role);
    setContractType(contract.contract_type);
    setFormData(contract.contract_json);
    setContractJson(contract.contract_json);
    setSelectedLanguage(contract.language || "en");
    setCurrentStep(2); // Set to input collection step
    setShowSavedContractsModal(false);
    history.push(url); // Navigate back to main contract generator
  };
  
  // View contract details
  const viewContractDetails = (contract) => {
    setSelectedContract(contract);
  };
  
  // Close contract details view
  const closeContractDetails = () => {
    setSelectedContract(null);
  };

  // Navigate to saved contracts view
  const navigateToSavedContracts = () => {
    history.push(`${url}/saved`);
  };

  // Navigate back to main contract generator
  const navigateToContractGenerator = () => {
    history.push(url);
  };

  const navigateToHome = useCallback(() => {
    setCurrentStep(0);
    setUserRole("");
    setContractType("");
    setFormData({});
    history.push(url);
  }, [history, url]);

  const submitContractToServer = useCallback(async (contractData) => {
    try {
      setLoading(true);
      setSubmissionError(null);

      const res = await generateContract(contractData);
      console.log('Server response:', res); // Debug log
      
      // For text responses, the content might be directly in res
      const contractText = res.data || res;
      
      if (!contractText) {
        console.error('Response structure:', res); // Debug log
        throw new Error('No contract text received from server');
      }

      // Set the response text directly
      setSubmissionResponse({
        contract_text: contractText
      });
      setCurrentStep(3); // Move to the json-output step
    } catch (error) {
      console.error('Error submitting contract:', error);
      setSubmissionError(error.message || 'Failed to generate contract');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFormSubmit = useCallback(async (newFormData) => {
    try {
      setFormData(newFormData);
      const contractData = {
        role: userRole,
        contract_type: contractType,
        language: selectedLanguage,
        contract_json: newFormData,
        timestamp: new Date().toISOString(),
      };
      
      setContractJson(contractData);
      await submitContractToServer(contractData);
    } catch (error) {
      console.error('Error in form submission:', error);
      setSubmissionError(error.message || 'Failed to submit form');
      setLoading(false); // Ensure loading is set to false on error
    }
  }, [userRole, contractType, selectedLanguage, submitContractToServer]);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const handleRoleSelect = (role) => {
    setUserRole(role);
    setFormData({});
    nextStep();
  };

  const handleContractSelect = (type) => {
    setContractType(type);
    nextStep();
  };

  // Add this function to handle form navigation
  const handleFormBack = useCallback(() => {
    setFormData({});
    prevStep();
  }, [prevStep]);

  const getProgressPercentage = () => {
    return ((currentStep + 1) / steps.length) * 100;
  };
  
  // Progress indicator with wizard icons
  const renderProgress = () => {
    const totalSteps = steps.length;
    const icons = [faUserTie, faFileContract, faClipboardCheck, faHandshake];
    const currentProgress = loading ? currentStep + 0.5 : currentStep;
    
    return (
      <div className="mb-4">
        <Row className="text-center mb-2">
          {wizardLabels.map((label, index) => (
            <Col key={index}>
              <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
                currentProgress > index ? 'bg-primary text-white' : 
                currentProgress === index ? 'bg-dark-accent text-primary border border-primary' : 
                'bg-dark-accent text-muted'
              }`} style={{width: '40px', height: '40px'}}>
                {currentProgress > index ? 
                  <FontAwesomeIcon icon={faCheck} size="sm" /> : 
                  loading && Math.floor(currentProgress) === index ? 
                    <Spinner animation="border" size="sm" /> :
                    <FontAwesomeIcon icon={icons[index]} size="sm" />
                }
              </div>
              <div className="mt-1">
                <small className={currentProgress >= index ? 'text-primary font-weight-bold' : 'text-muted'}>
                  {label}
                </small>
              </div>
            </Col>
          ))}
        </Row>
        <ProgressBar now={((currentProgress + 1) / totalSteps) * 100} variant="primary" />
      </div>
    );
  };

  const handlePrint = useCallback(() => {
    printContract(submissionResponse?.contract_text || '');
  }, [submissionResponse]);

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(submissionResponse?.contract_text || '');
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [submissionResponse]);

  // Main Contract Generator Component
  const MainContractGenerator = () => {
    const renderStep = () => {
      switch (steps[currentStep]) {
        case "role-selection":
          return (
            <Card className="dark-main-card text-white mb-4">
              <Card.Body className="p-4">
                <h3 className="mb-4 text-white" style={{ fontSize: "1.5rem" }}>Select Your Role</h3>
                <p className="text-white mb-4">
                  Choose the role that best describes your position in the event or production.
                </p>
                
                <Row>
                  {roles.map((role) => (
                    <Col md={6} key={role.id} className="mb-3">
                      <Card 
                        className={`dark-card cursor-pointer ${userRole === role.id ? 'border-primary' : ''}`} 
                        onClick={() => handleRoleSelect(role.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="d-flex justify-content-between align-items-center">
                          <span className="text-white">{role.label}</span>
                          {userRole === role.id && (
                            <FontAwesomeIcon icon={faCheck} className="text-primary" />
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          );
        case "contract-type":
          return (
            <Card className="dark-main-card text-white mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h3 className="mb-0 text-white" style={{ fontSize: "1.5rem" }}>Select Contract Type</h3>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <label className="mb-0 text-light">Output Language:</label>
                      <Form.Select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        style={{ 
                          width: 'auto', 
                          backgroundColor: '#212529', 
                          color: 'white',
                          padding: '0.375rem 2.25rem 0.375rem 0.75rem'
                        }}
                      >
                        {supportedLanguages.map(lang => (
                          <option
                            key={lang.id}
                            value={lang.id}
                            style={{ 
                              backgroundColor: '#212529', 
                              color: 'white',
                              padding: '4px 8px'
                            }}
                          >
                            {lang.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      className="dark-outline-button"
                      onClick={prevStep}
                    >
                      Back
                    </Button>
                  </div>
                </div>
                
                <p className="text-white mb-4">
                  Choose the type of contract you need to generate.
                </p>
                
                <Row>
                  {contractTypes[userRole]?.map((contract) => (
                    <Col md={6} key={contract.id} className="mb-3">
                      <Card 
                        className={`dark-card cursor-pointer ${contractType === contract.id ? 'border-primary' : ''}`} 
                        onClick={() => handleContractSelect(contract.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="d-flex justify-content-between align-items-center">
                          <span className="text-white">{contract.label}</span>
                          {contractType === contract.id && (
                            <FontAwesomeIcon icon={faCheck} className="text-primary" />
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          );
        case "input-collection":
          return (
            <Card className="dark-main-card text-light mb-4">
              <Card.Body className="p-4">
                <Card.Title as="h2" className="mb-4" style={{ fontSize: "1.5rem" }}>
                  Enter Contract Details
                </Card.Title>
                <Card.Text className="mb-4 text-primary">
                  Please provide all necessary information to generate your contract.
                </Card.Text>
                <ContractForm 
                  fields={getFormFields(userRole, contractType)}
                  initialFormData={formData}
                  onInputChange={handleFormSubmit}
                  onBack={handleFormBack}
                  isLoading={loading}
                />
              </Card.Body>
            </Card>
          );
        case "json-output":
          return (
            <Card className="dark-main-card text-light mb-4">
              <Card.Body className="p-4">
                <Card.Title as="h2" className="mb-4" style={{ fontSize: "1.5rem" }}>
                  Generated Contract
                </Card.Title>
                <p style={{ 
                  color: '#5fb4de', 
                  fontSize: '0.9rem', 
                  marginBottom: '1.5rem',
                  fontStyle: 'italic'
                }}>
                  Disclaimer: All contracts are contingent upon the accuracy of the data provided. We recommend consulting with a local legal professional for further guidance.
                </p>
                
                {submissionError ? (
                  <Alert variant="danger" className="dark-alert mb-4">
                    <h5>Error Generating Contract</h5>
                    <p>{submissionError}</p>
                    <Button 
                      variant="outline-light" 
                      size="sm"
                      onClick={() => submitContractToServer(contractJson)}
                    >
                      Retry Submission
                    </Button>
                  </Alert>
                ) : loading ? (
                  <div className="text-center">
                    <Spinner
                      animation="border"
                      variant="primary"
                      className="mb-4"
                    />
                    <p>Generating your contract...</p>
                  </div>
                ) : submissionResponse ? (
                  <>
                    <Card.Text className="mb-4">
                      Your contract has been generated successfully. Please review the details below:
                    </Card.Text>
                    <div className="dark-card-alt p-3 overflow-auto mb-4" style={{ 
                      maxHeight: "600px",
                      background: "rgba(0, 0, 0, 0.2)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px"
                    }}>
                      <pre className="text-light" style={{ 
                        whiteSpace: "pre-wrap",
                        fontFamily: "'Courier New', monospace",
                        fontSize: "0.9em",
                        lineHeight: "1.5"
                      }}>
                        {submissionResponse.contract_text || 'No contract text available'}
                      </pre>
                    </div>
                    <div className="mt-4 d-flex justify-content-between">
                      <Button className="dark-outline-button" onClick={prevStep}>
                        Back
                      </Button>
                      <div className="d-flex gap-2">
                        <Button 
                          className="dark-primary-button" 
                          onClick={handlePrint}
                        >
                          <FontAwesomeIcon icon={faPrint} className="me-2" />
                          Print
                        </Button>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="copy-tooltip">
                              {copySuccess ? 'Copied!' : 'Copy to clipboard'}
                            </Tooltip>
                          }
                        >
                          <Button 
                            className="dark-primary-button" 
                            onClick={handleCopy}
                          >
                            <FontAwesomeIcon icon={faCopy} className="me-2" />
                            {copySuccess ? 'Copied!' : 'Copy'}
                          </Button>
                        </OverlayTrigger>
                        {submissionResponse.downloadUrl && (
                          <Button 
                            className="dark-primary-button" 
                            href={submissionResponse.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download Contract
                          </Button>
                        )}
                        <Button
                          className="dark-outline-button"
                          onClick={() => setCurrentStep(0)}
                        >
                          Create Another Contract
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <Alert variant="danger" className="dark-alert mb-4">
                    <h5>Error</h5>
                    <p>No contract data available. Please try generating the contract again.</p>
                    <Button 
                      variant="outline-light" 
                      size="sm"
                      onClick={() => submitContractToServer(contractJson)}
                    >
                      Retry Submission
                    </Button>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          );
        default:
          return null;
      }
    };
    
    return (
      <>
        <Breadcrumb className="dark-breadcrumb mb-4" style={{ color: "white" }}>
          <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
          <Breadcrumb.Item active>Contract Generator</Breadcrumb.Item>
        </Breadcrumb>

        <Container fluid className="budget-planner-container py-4 min-vh-100">
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="mb-5 text-center">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <p 
                    className="display-5 mb-0 text-primary cursor-pointer" 
                    style={{ 
                      fontWeight: 200, 
                      textShadow: '0 0 10px rgba(79, 209, 197, 0.3)', 
                      fontSize: "1.8rem",
                      cursor: "pointer" 
                    }}
                    onClick={navigateToHome}
                  >
                    AI Contract Generator
                  </p>
                  <Button 
                    variant="outline-primary"
                    className="dark-outline-button"
                    onClick={() => navigateToSavedContracts()}
                  >
                    Saved Contracts
                  </Button>
                </div>
              
                {renderProgress()}
              </div>

              {renderStep()}
            </Col>
          </Row>
        </Container>
      </>
    );
  };

  return (
    <>
      <Switch>
        <Route exact path={path}>
          <MainContractGenerator />
          <ContractDetailsModal 
            contract={selectedContract}
            onHide={closeContractDetails}
            onLoad={loadContract}
          />
        </Route>
        <Route path={`${path}/saved`}>
          <SavedContracts 
            savedContracts={savedContracts}
            onLoad={loadContract}
            onView={viewContractDetails}
            onBack={navigateToContractGenerator}
          />
          <ContractDetailsModal 
            contract={selectedContract}
            onHide={closeContractDetails}
            onLoad={loadContract}
          />
        </Route>
      </Switch>
    </>
  );
};

export default ContractGenerator;
